export type HtmlSeoAuditRoute = {
    route: string;
    title: string;
    description: string[];
    canonical: string[];
    viewport: string[];
    ogImage: string[];
    twitterImage: string[];
    h1: string[];
    jsonLdCount: number;
    jsonLdTypes: string[];
    jsonLdValid: boolean;
};

export type HtmlSeoAuditIssue = {
    route: string;
    issues: string[];
};

const cleanWhitespace = (value: string) => value.replace(/\s+/g, ' ').trim();

const stripTags = (value: string) =>
    cleanWhitespace(value.replace(/<script[\s\S]*?<\/script>/gi, ' ').replace(/<style[\s\S]*?<\/style>/gi, ' ').replace(/<[^>]+>/g, ' '));

const getAttributes = (tag: string) => {
    const attributes: Record<string, string> = {};
    const attributePattern = /([a-zA-Z_:][-a-zA-Z0-9_:.]*)\s*=\s*(?:"([^"]*)"|'([^']*)')/g;
    let match: RegExpExecArray | null;

    while ((match = attributePattern.exec(tag))) {
        attributes[match[1].toLowerCase()] = match[2] ?? match[3] ?? '';
    }

    return attributes;
};

const getMetaContent = (html: string, selectorName: string) => {
    const lowerSelector = selectorName.toLowerCase();
    const tags = html.match(/<meta\b[^>]*>/gi) || [];

    return tags
        .map(getAttributes)
        .filter((attributes) => attributes.name?.toLowerCase() === lowerSelector || attributes.property?.toLowerCase() === lowerSelector)
        .map((attributes) => attributes.content || '')
        .filter(Boolean);
};

const getCanonical = (html: string) =>
    (html.match(/<link\b[^>]*>/gi) || [])
        .map(getAttributes)
        .filter((attributes) => attributes.rel?.toLowerCase() === 'canonical')
        .map((attributes) => attributes.href || '')
        .filter(Boolean);

const getTitle = (html: string) => {
    const match = html.match(/<title[^>]*>([\s\S]*?)<\/title>/i);
    return match ? stripTags(match[1]) : '';
};

const getH1Texts = (html: string) =>
    (html.match(/<h1\b[\s\S]*?<\/h1>/gi) || []).map(stripTags).filter(Boolean);

const schemaTypes = (schema: unknown): string[] => {
    const types: string[] = [];

    const visit = (value: unknown) => {
        if (Array.isArray(value)) {
            value.forEach(visit);
            return;
        }

        if (!value || typeof value !== 'object') return;

        const record = value as Record<string, unknown>;
        const type = record['@type'];

        if (Array.isArray(type)) {
            types.push(...type.map(String));
        } else if (type) {
            types.push(String(type));
        }

        if (Array.isArray(record['@graph'])) {
            record['@graph'].forEach(visit);
        }
    };

    visit(schema);
    return types;
};

const getJsonLd = (html: string) => {
    const scripts = html.match(/<script\b[^>]*type=["']application\/ld\+json["'][^>]*>[\s\S]*?<\/script>/gi) || [];

    return scripts.map((script) => {
        const body = script.replace(/^<script\b[^>]*>/i, '').replace(/<\/script>$/i, '').trim();

        try {
            const parsed = JSON.parse(body);
            return {
                ok: true,
                types: schemaTypes(parsed),
            };
        } catch {
            return {
                ok: false,
                types: [],
            };
        }
    });
};

export const auditHtmlSeo = (html: string, route = '/') : HtmlSeoAuditRoute => {
    const jsonLd = getJsonLd(html);

    return {
        route,
        title: getTitle(html),
        description: getMetaContent(html, 'description'),
        canonical: getCanonical(html),
        viewport: getMetaContent(html, 'viewport'),
        ogImage: getMetaContent(html, 'og:image'),
        twitterImage: getMetaContent(html, 'twitter:image'),
        h1: getH1Texts(html),
        jsonLdCount: jsonLd.length,
        jsonLdTypes: jsonLd.flatMap((entry) => entry.types),
        jsonLdValid: jsonLd.every((entry) => entry.ok),
    };
};

export const getHtmlSeoAuditIssues = (route: HtmlSeoAuditRoute): HtmlSeoAuditIssue | null => {
    const issues: string[] = [];

    if (route.viewport.length !== 1) issues.push(`viewport:${route.viewport.length}`);
    if (route.canonical.length !== 1) issues.push(`canonical:${route.canonical.length}`);
    if (route.description.length !== 1) issues.push(`description:${route.description.length}`);
    if (route.ogImage.length < 1) issues.push('og:image:0');
    if (route.twitterImage.length < 1) issues.push('twitter:image:0');
    if (route.h1.length !== 1) issues.push(`h1:${route.h1.length}`);
    if (route.jsonLdCount < 1) issues.push('jsonld:0');
    if (!route.jsonLdValid) issues.push('jsonld:invalid');

    return issues.length ? { route: route.route, issues } : null;
};
